import { Injectable } from '@angular/core';
import {
  addDays,
  addMonths,
  addQuarters,
  addWeeks,
  addYears,
  differenceInDays,
  endOfMonth,
  startOfMonth,
  subDays,
  subMonths,
  subQuarters,
  subWeeks,
  subYears,
} from 'date-fns';
import { chunk } from 'lodash';
import { BenchmarkPeriodOption } from 'app/hub-components/components/benchmarks/benchmarks.component';
import { Row } from '../../models/analytics-api/query_result.model';
import { DatesService } from '../../modules/date-range-picker/dates.service';
import { DateRange, DateRangeValue, FixedDateRange } from '../../modules/date-range-picker/types';

@Injectable()
export class BenchmarkService {
  constructor(
    private datesService: DatesService) { }

  calculateBenchmark(dateInterval: DateRangeValue, benchmark: string, benchmarkDateRange: DateRangeValue = undefined, queryFirstDayOfWeek: number = undefined): DateRange {
    let dates: DateRange;
    if (dateInterval) {
      switch (benchmark) {
        case 'previous_period':
          dates = this.previousPeriod(dateInterval, queryFirstDayOfWeek);
          break;
        case 'previous_year':
          dates = this.previousYear(dateInterval, queryFirstDayOfWeek);
          break;
        case 'custom':
          dates = this.datesService.parseValue(benchmarkDateRange, queryFirstDayOfWeek);
          break;
      }
    }
    return { ...dates };
  }

  createBenchmarkPages(rows, benchmarks, resultsPerPage, transpose = 'rows') {
    const mapped = benchmarks.map((b, index) => {
      if (b) {
        const row = rows[index];
        return row.metrics.reduce((memo, metric) => {
          const n = metric.name;
          let diff;
          if (transpose === 'columns') {
            diff = metric.values.map((value, idx) => this.diff(value, b[n][idx]));
          } else {
            diff = this.diff(metric.value, b[n]);
          }
          memo[n] = diff;
          return memo;
        }, {});
      } else {
        return {};
      }
    });
    return chunk(mapped, resultsPerPage).map(group => group.filter(Boolean));
  }

  getSortedUniqueDatesFromRows(rows: Row[]): string[] {
    return rows
      .reduce(
        (unique, row) =>
          unique.includes(row.date.getTime().toString())
            ? unique
            : [...unique, row.date.getTime().toString()],
        [],
      )
      .sort((dateA, dateB) => dateA - dateB);
  }

  public diff(value: number, benchmark: number): number {
    if (value !== null && benchmark > 0) {
      let diff = value / benchmark;
      diff -= 1;
      return diff;
    }
  }

  public getCustomPeriodDefaultDateRange(dateInterval: DateRangeValue, defaultPeriod: BenchmarkPeriodOption, queryFirstDayOfWeek: number): FixedDateRange {
    const period = this.calculateBenchmark(
      dateInterval,
      defaultPeriod.value,
      undefined,
      queryFirstDayOfWeek,
    );

    return this.datesService.dateRangeToFixedDateRange({
      startDate: period.startDate,
      endDate: period.endDate,
    });
  }

  private previousPeriod(dateInterval: DateRangeValue, queryFirstDayOfWeek: number) {
    const isDynamic = dateInterval.type === 'dynamic' || dateInterval['dynamic'];
    if (isDynamic) {
      return this.calculateDynamicPreviousPeriod(dateInterval, queryFirstDayOfWeek);
    } else {
      return this.calculateFixedPreviousPeriod(dateInterval, queryFirstDayOfWeek);
    }
  }

  private calculateDynamicPreviousPeriod(dateInterval: DateRangeValue, queryFirstDayOfWeek: number) {
    const base = this.datesService.parseValue(dateInterval, queryFirstDayOfWeek);
    const dynamic = dateInterval['dynamic'];

    if (dynamic.name === 'yesterday') {
      const yesterday = subDays(new Date(base.startDate.getTime()), 1);
      return {
        startDate: yesterday,
        endDate: yesterday,
      };
    } else {
      const startDate = this.addUnit(new Date(base.startDate.getTime()), dynamic.unit, -dynamic.num);
      let endDate = subDays(new Date(base.startDate.getTime()), 1);

      if (dynamic.toDate && this.supportsToDate(dynamic.name, dynamic.unit)) {
        endDate = this.adjustEndDateForDynamic(base, startDate, endDate, dynamic.unit);
      }

      return {
        startDate,
        endDate,
      };
    }
  }

  private supportsToDate(name: string, unit: string): boolean {
    return (name === 'this' && (unit === 'month' || unit === 'quarter' || unit === 'year'));
  }

  private addUnit(date: Date, unit: string, amount: number): Date {
    switch (unit) {
      case 'day': return amount >= 0 ? addDays(date, amount) : subDays(date, -amount);
      case 'week': return amount >= 0 ? addWeeks(date, amount) : subWeeks(date, -amount);
      case 'month': return amount >= 0 ? addMonths(date, amount) : subMonths(date, -amount);
      case 'quarter': return amount >= 0 ? addQuarters(date, amount) : subQuarters(date, -amount);
      case 'year': return amount >= 0 ? addYears(date, amount) : subYears(date, -amount);
      default: return date;
    }
  }

  private adjustEndDateForDynamic(base: DateRange, startDate: Date, endDate: Date, unit: string) {
    if (unit === 'month' || unit === 'quarter') {
      const monthsBetween = base.endDate.getMonth() - base.startDate.getMonth();
      const adjustedEnd = unit === 'quarter'
        ? startOfMonth(addMonths(startDate, monthsBetween))
        : new Date(endDate.getTime());
      const endOfMonthDate = endOfMonth(adjustedEnd);
      adjustedEnd.setDate(Math.min(base.endDate.getDate(), endOfMonthDate.getDate()));
      return adjustedEnd;
    } else {
      const adjustedEnd = new Date(
        startDate.getFullYear(),
        base.endDate.getMonth(),
        1,
      );
      const endOfTargetMonth = endOfMonth(adjustedEnd);
      adjustedEnd.setDate(Math.min(base.endDate.getDate(), endOfTargetMonth.getDate()));
      return adjustedEnd;
    }
  }

  private calculateFixedPreviousPeriod(dateInterval: DateRangeValue, queryFirstDayOfWeek: number) {
    const dates = this.datesService.parseValue(dateInterval, queryFirstDayOfWeek);
    const daysBetween = differenceInDays(dates.endDate, dates.startDate) + 1;
    return {
      startDate: subDays(new Date(dates.startDate.getTime()), daysBetween),
      endDate: subDays(new Date(dates.endDate.getTime()), daysBetween),
    };
  }

  private previousYear(dateInterval: DateRangeValue, queryFirstDayOfWeek: number): DateRange {
    const dates = this.datesService.parseValue(dateInterval, queryFirstDayOfWeek);
    const startDate = new Date(dates.startDate.getTime());
    const endDate = new Date(dates.endDate.getTime());
    const yearsSpan = endDate.getFullYear() - startDate.getFullYear();

    endDate.setFullYear(endDate.getFullYear() - 1);
    startDate.setFullYear(endDate.getFullYear() - yearsSpan);

    return { startDate, endDate };
  }
}
